import api from './api';

const API_BASE = '/workroom';

export interface CollabTask {
  id: string;
  userId: string;
  userName: string;
  command: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: string;
  resultType?: string;
  resultFileId?: string;
  resultFileName?: string;
  summary?: string;
  /** AI 检索到的知识文件 JSON 字符串（[{name,score}]） */
  kbDocs?: string | null;
  totalTokens: number;
  elapsedMs: number;
  attachedFileName?: string;
  fileContent?: string;
  thinkingContent?: string;
  isPrivate?: boolean;
  createdAt: string;
  sessionId?: string | null;
  updatedAt: string;
}

export interface WorkSession {
  id: string;
  userId: string;
  title: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  taskCount?: number;
  lastCommand?: string;
  tasks?: CollabTask[];
}

export interface TaskProgress {
  taskId: string;
  step: string;
  detail: string;
  docs?: { name: string; score: number }[];
  experience?: { id: string; question: string; summary: string; score: number; source: string }[];
  timestamp: number;
}

export interface FilePreview {
  type: 'csv' | 'doc' | 'text';
  headers?: string[];
  rows?: string[][];
  content?: string;
  error?: string;
}

/** 创建新任务 */
export async function createCollabTask(
  command: string, resultType: string = 'text', kbDepth: number = 20,
  fileContent?: string, fileName?: string, taskType?: string, useKb?: boolean,
  isPrivate?: boolean,
  enableThinking?: boolean,
  modelId?: string,
  sessionId?: string | null,
): Promise<CollabTask> {
  const { data } = await api.post(`${API_BASE}/tasks`, { command, resultType, kbDepth, fileContent, fileName, taskType, useKb, isPrivate, enableThinking, modelId, sessionId });
  return data;
}

export async function copyCollabTask(taskId: string, command?: string): Promise<CollabTask> {
  const { data } = await api.post(`${API_BASE}/tasks/copy`, { taskId, command });
  return data;
}

export async function getRecentTasks(skip: number = 0, take: number = 10, search?: string, sessionId?: string | null): Promise<CollabTask[]> {
  const params: any = { skip, take };
  if (search) params.search = search;
  if (sessionId) params.sessionId = sessionId;
  const { data } = await api.get(`${API_BASE}/tasks`, { params });
  return data;
}

export async function getTaskDetail(taskId: string): Promise<CollabTask> {
  const { data } = await api.get(`${API_BASE}/tasks/${taskId}`);
  return data;
}

export async function getWorkSessions(includeArchived = false): Promise<WorkSession[]> {
  const { data } = await api.get('/workroom/sessions', { params: { includeArchived } });
  return data;
}

export async function createWorkSession(title?: string): Promise<WorkSession> {
  const { data } = await api.post('/workroom/sessions', { title });
  return data;
}

export async function getWorkSessionDetail(id: string): Promise<WorkSession> {
  const { data } = await api.get(`/workroom/sessions/${id}`);
  return data;
}

export async function updateWorkSession(id: string, payload: Partial<Pick<WorkSession, 'title' | 'isArchived'>>): Promise<WorkSession> {
  const { data } = await api.patch(`/workroom/sessions/${id}`, payload);
  return data;
}

export async function deleteWorkSession(id: string): Promise<{ success: boolean }> {
  const { data } = await api.delete(`/workroom/sessions/${id}`);
  return data;
}

export function getFileDownloadUrl(fileId: string): string {
  return `/api${API_BASE}/files/${fileId}`;
}

export function getFilePreviewUrl(fileId: string): string {
  return `/api${API_BASE}/files/${fileId}/preview`;
}

/** 上传文件并解析 */
// 上传接口改为 SSE 流式响应：识别过程中不断收到 progress 事件（保活+进度），最后一个 done 事件带完整结果。
// 这样大PDF逐页OCR(1-2分钟)也不会因连接空闲被上游代理掐断。
export async function uploadCollabFile(
  file: File,
  onProgress?: (msg: string) => void,
): Promise<{ success: boolean; fileName?: string; content?: string; charCount?: number; error?: string; tokenEstimate?: number }> {
  const formData = new FormData();
  formData.append('file', file);
  let token = '';
  try { const s = JSON.parse(localStorage.getItem('auth-storage') || '{}'); token = s?.state?.token || ''; } catch {}
  const response = await fetch(`/api${API_BASE}/files/upload`, {
    method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: formData,
  });
  if (!response.ok || !response.body) {
    const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
    throw new Error(err.message || `HTTP ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result: any = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const rawEvent = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let ev = 'message';
      let data = '';
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) ev = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) continue; // 注释心跳行(: hb)跳过
      let parsed: any;
      try { parsed = JSON.parse(data); } catch { continue; }
      if (ev === 'progress') onProgress?.(parsed.message || '');
      else if (ev === 'done') result = parsed;
    }
  }
  return result || { success: false, error: '上传无响应' };
}

/** 获取文件预览数据 */
export async function getFilePreview(fileId: string): Promise<FilePreview> {
  const { data } = await api.get(`${API_BASE}/files/${fileId}/preview`);
  return data;
}

export interface GeneratedCollabFile {
  success: boolean;
  fileId?: string;
  resultFileId?: string;
  resultFileName?: string;
  error?: string;
}

/** 将已完成任务的结果生成Word文档 */
export async function generateWordFromTask(taskId: string): Promise<GeneratedCollabFile> {
  const { data } = await api.post(`${API_BASE}/tasks/${taskId}/generate-word`, undefined, { timeout: 120000 });
  return data;
}

/** 将任意文本生成Word文档 */
export async function generateWordFromText(title: string, content: string): Promise<GeneratedCollabFile> {
  const { data } = await api.post(`${API_BASE}/generate-word-from-text`, { title, content }, { timeout: 120000 });
  return data;
}

/** 将已完成任务的结果生成PDF文档 */
export async function generatePdfFromTask(taskId: string): Promise<GeneratedCollabFile> {
  const { data } = await api.post(`${API_BASE}/tasks/${taskId}/generate-pdf`, undefined, { timeout: 120000 });
  return data;
}

/** 将任意文本生成PDF文档 */
export async function generatePdfFromText(title: string, content: string): Promise<GeneratedCollabFile> {
  const { data } = await api.post(`${API_BASE}/generate-pdf-from-text`, { title, content }, { timeout: 120000 });
  return data;
}
